import {
  useCallback, useEffect, useRef, useState,
} from 'react'
import { createOfficeGoogleSession } from '../../modules/User'

export const OFFICE_GOOGLE_LOGIN_HOSTNAMES = [
  'test_demo_biz.office.mamorio.jp',
  'tepsco.office.mamorio.jp',
]

const GOOGLE_AUTH_MESSAGE_TYPE = 'mamorio:office-google-auth'
const POPUP_CHECK_INTERVAL_MS = 500
const AUTH_TIMEOUT_MS = 5 * 60 * 1000
const API_TIMEOUT_MS = 30 * 1000

const GOOGLE_LOGIN_ERROR_MESSAGE = 'Googleログインに失敗しました。もう一度お試しください。'
const POPUP_BLOCKED_ERROR_MESSAGE = 'ポップアップがブロックされました。ブラウザの設定を確認して、もう一度お試しください。'
const POPUP_CLOSED_ERROR_MESSAGE = 'Googleログインがキャンセルされました。もう一度お試しください。'
const GOOGLE_LOGIN_TIMEOUT_ERROR_MESSAGE = 'Googleログインがタイムアウトしました。もう一度お試しください。'

type OfficeGoogleAuthMessage = {
  type?: unknown
  flow_id?: unknown
  credential?: unknown
  office_google_context?: unknown
}

type AuthAttempt = {
  popup: Window
  flowId: string
  messageListener: (event: MessageEvent<OfficeGoogleAuthMessage>) => void
  popupCheckTimer?: number
  authTimeoutTimer?: number
  apiTimeoutTimer?: number
  abortController?: AbortController
}

type UseOfficeGoogleLoginProps = {
  onError: (message: string) => void
}

const getOfficeHostname = (): string => process.env.REACT_APP_HOSTNAME || window.location.hostname

const isLocalHostname = (hostname: string): boolean => (
  hostname === 'localhost'
  || hostname.endsWith('.localhost')
  || hostname === '127.0.0.1'
  || hostname === '::1'
  || hostname === '[::1]'
)

export const isOfficeGoogleLoginEnabled = (
  browserHostname = window.location.hostname,
  officeHostname = getOfficeHostname(),
): boolean => (
  isLocalHostname(browserHostname)
  || OFFICE_GOOGLE_LOGIN_HOSTNAMES.includes(officeHostname)
)

const generateFlowId = (): string => {
  const values = new Uint32Array(4)
  window.crypto.getRandomValues(values)
  return Array.from(values)
    .map((value) => value.toString(16).padStart(8, '0'))
    .join('')
}

const getAuthHubOrigin = (): string => {
  const basePath = process.env.REACT_APP_BASE_PATH
  if (!basePath) throw new Error('REACT_APP_BASE_PATH is not configured')

  return new URL(basePath).origin
}

const buildAuthHubUrl = (authHubOrigin: string, flowId: string): string => {
  const url = new URL('/users/auth/google/office', authHubOrigin)
  url.searchParams.set('hostname', getOfficeHostname())
  url.searchParams.set('flow_id', flowId)
  return url.toString()
}

const useOfficeGoogleLogin = ({ onError }: UseOfficeGoogleLoginProps) => {
  const [isGoogleLoginInProgress, setIsGoogleLoginInProgress] = useState(false)
  const attemptRef = useRef<AuthAttempt | null>(null)

  const clearPopupPhase = useCallback((attempt: AuthAttempt) => {
    window.removeEventListener('message', attempt.messageListener)

    if (attempt.popupCheckTimer !== undefined) {
      window.clearInterval(attempt.popupCheckTimer)
    }
    if (attempt.authTimeoutTimer !== undefined) {
      window.clearTimeout(attempt.authTimeoutTimer)
    }
    if (!attempt.popup.closed) attempt.popup.close()
  }, [])

  const finishAttempt = useCallback((attempt: AuthAttempt, abortRequest = false) => {
    if (attemptRef.current !== attempt) return false

    clearPopupPhase(attempt)
    if (attempt.apiTimeoutTimer !== undefined) {
      window.clearTimeout(attempt.apiTimeoutTimer)
    }
    if (abortRequest) attempt.abortController?.abort()

    attemptRef.current = null
    setIsGoogleLoginInProgress(false)
    return true
  }, [clearPopupPhase])

  const startGoogleLogin = useCallback(() => {
    if (!isOfficeGoogleLoginEnabled() || attemptRef.current !== null) return

    let authHubOrigin: string
    let flowId: string
    try {
      authHubOrigin = getAuthHubOrigin()
      flowId = generateFlowId()
    } catch (_) {
      onError(GOOGLE_LOGIN_ERROR_MESSAGE)
      return
    }

    const popup = window.open(
      buildAuthHubUrl(authHubOrigin, flowId),
      'mamorio-office-google-auth',
      'popup=yes,width=520,height=650,resizable=yes,scrollbars=yes',
    )

    if (!popup) {
      onError(POPUP_BLOCKED_ERROR_MESSAGE)
      return
    }

    const attempt: AuthAttempt = {
      popup,
      flowId,
      messageListener: () => {},
    }

    const handleMessage = async (event: MessageEvent<OfficeGoogleAuthMessage>) => {
      if (
        attemptRef.current !== attempt
        || event.origin !== authHubOrigin
        || event.source !== popup
        || event.data?.type !== GOOGLE_AUTH_MESSAGE_TYPE
        || event.data.flow_id !== flowId
      ) return

      const { credential, office_google_context: officeGoogleContext } = event.data
      if (typeof credential !== 'string' || !credential || typeof officeGoogleContext !== 'string' || !officeGoogleContext) {
        if (finishAttempt(attempt)) onError(GOOGLE_LOGIN_ERROR_MESSAGE)
        return
      }

      clearPopupPhase(attempt)
      attempt.abortController = new AbortController()
      attempt.apiTimeoutTimer = window.setTimeout(() => {
        attempt.abortController?.abort()
      }, API_TIMEOUT_MS)

      try {
        await createOfficeGoogleSession(
          credential,
          officeGoogleContext,
          attempt.abortController.signal,
        )
        finishAttempt(attempt)
      } catch (_) {
        if (finishAttempt(attempt)) onError(GOOGLE_LOGIN_ERROR_MESSAGE)
      }
    }

    attempt.messageListener = handleMessage
    attemptRef.current = attempt
    setIsGoogleLoginInProgress(true)
    window.addEventListener('message', handleMessage)

    attempt.popupCheckTimer = window.setInterval(() => {
      if (popup.closed && finishAttempt(attempt)) {
        onError(POPUP_CLOSED_ERROR_MESSAGE)
      }
    }, POPUP_CHECK_INTERVAL_MS)

    attempt.authTimeoutTimer = window.setTimeout(() => {
      if (finishAttempt(attempt, true)) onError(GOOGLE_LOGIN_TIMEOUT_ERROR_MESSAGE)
    }, AUTH_TIMEOUT_MS)
  }, [clearPopupPhase, finishAttempt, onError])

  useEffect(() => () => {
    const attempt = attemptRef.current
    if (!attempt) return

    window.removeEventListener('message', attempt.messageListener)
    if (attempt.popupCheckTimer !== undefined) window.clearInterval(attempt.popupCheckTimer)
    if (attempt.authTimeoutTimer !== undefined) window.clearTimeout(attempt.authTimeoutTimer)
    if (attempt.apiTimeoutTimer !== undefined) window.clearTimeout(attempt.apiTimeoutTimer)
    if (!attempt.popup.closed) attempt.popup.close()
    attempt.abortController?.abort()
    attemptRef.current = null
  }, [])

  return {
    isGoogleLoginInProgress,
    startGoogleLogin,
  }
}

export default useOfficeGoogleLogin
